import * as React from 'react'

import owlShape from '../assets/owl/owl-head-shape.svg?url'
import owlBorder from '../assets/owl/owl-head-border.svg?url'
import styles from './OwlSlideshow.module.css'

// Every portrait in the folder — adding/removing a file there is the whole update (picked up at build
// time via Vite's glob), so new/ex coworkers are handled without touching code.
const modules = import.meta.glob('../assets/owl-slideshow/*.{jpg,jpeg,png,webp}', {
  eager: true,
  query: '?url',
  import: 'default',
})
const IMAGES: string[] = Object.keys(modules)
  .sort()
  .map((path) => modules[path])

const INTERVAL_MS = 2500

// A deterministic ("seeded") shuffle: the portraits play in a random-looking order that is the SAME on
// every page load — and identical for SSR + hydration — rather than re-randomising as it cycles.
// Adding/removing a file re-derives the order.
function seededSequence(length: number): number[] {
  let seed = 0x9e3779b9
  const random = (): number => {
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
  const order = Array.from({ length }, (_, index) => index)
  for (let i = length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    ;[order[i], order[j]] = [order[j], order[i]]
  }
  return order
}
const SEQUENCE = seededSequence(IMAGES.length)
const FIRST = SEQUENCE[0] ?? 0

/**
 * Cycles the team portraits, each clipped to the Ulule owl-head silhouette (CSS mask + object-fit:
 * cover, so no overflow), with a 2px ink border and a hard "sticker" drop-shadow — like the bold cards.
 * Plays a fixed, seeded (deterministic) order, so it's stable within and across page loads. The incoming
 * photo fades in ON TOP of the previous one (which stays opaque underneath) so the shaped shadow/border
 * never flicker.
 */
export function OwlSlideshow(): React.ReactElement {
  // Start with prev === active so only ONE image is visible on first paint (no decode race flashing a
  // stray second image); prev becomes the outgoing image once cycling begins.
  const [frame, setFrame] = React.useState({ active: FIRST, prev: FIRST })

  React.useEffect(() => {
    if (SEQUENCE.length <= 1) return
    let pointer = 0
    const timer = window.setInterval(() => {
      pointer = (pointer + 1) % SEQUENCE.length
      setFrame((current) => ({ active: SEQUENCE[pointer], prev: current.active }))
    }, INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [])

  const { active, prev } = frame

  return (
    <div
      className={styles.owl}
      role="img"
      aria-label="Portraits de l'équipe Ulule"
      style={{ '--owl-mask': `url("${owlShape}")`, '--owl-border': `url("${owlBorder}")` } as React.CSSProperties}
    >
      <div className={styles.owl__stack}>
        {IMAGES.map((src, index) => (
          <img
            key={src}
            className={styles.owl__img}
            src={src}
            alt=""
            aria-hidden="true"
            loading={index === FIRST ? 'eager' : 'lazy'}
            decoding="async"
            style={{
              opacity: index === active || index === prev ? 1 : 0,
              zIndex: index === active ? 2 : index === prev ? 1 : 0,
            }}
          />
        ))}
      </div>
      <div className={styles.owl__border} aria-hidden="true" />
    </div>
  )
}
